import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MessageSquare, Send, Search, Star, MapPin, X, Paperclip, FileText, Download, Check, CheckCheck, Eye, Loader2, Package, Building2, Clock, ArrowUp, CheckCircle, DollarSign, Mail, Copy, ArrowLeft } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { directoryApi, chatApi, uploadsApi, quotesApi } from '../services/api';
import { getLogoOrInitials } from '../utils/companyLogo';
import InquiryCreatorIdentity from './InquiryCreatorIdentity';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const getFullFileUrl = (url) => (url && url.startsWith('/') ? `${API_BASE}${url}` : url);

const MAX_FILE_SIZE_MB = parseInt(process.env.REACT_APP_MAX_FILE_SIZE_MB || '10', 10);
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const IMAGE_EXTENSIONS = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp']);
const ALLOWED_CHAT_FILES = 'image/*,.pdf,.doc,.docx,.xls,.xlsx';
import { useAuth } from '../context/AuthContext';
import { useTier } from '../context/TierContext';
import { useChatWebSocketContext } from '../context/ChatWebSocketContext';
import RestrictedOverlay from './ui/RestrictedOverlay';
import { getErrorMessage } from '../lib/utils';
import { toast } from 'sonner';

const mapBackendMessageToFrontend = (msg, currentMemberId) => {
  const d = msg.sent_at ? new Date(msg.sent_at) : new Date();
  const ext = (msg.file_url || '').split('.').pop()?.toLowerCase();
  const messageType = msg.message_type || (ext && IMAGE_EXTENSIONS.has(ext) ? 'Image' : msg.file_url ? 'File' : 'Text');
  return {
    message_id: msg.message_id || msg.id,
    sender: msg.sender_id === currentMemberId ? 'me' : 'other',
    text: msg.content,
    time: d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    sent_at: msg.sent_at || d.toISOString(),
    message_type: messageType,
    file_url: msg.file_url,
    read_by: msg.read_by || [],
  };
};

const getDateLabel = (sentAt) => {
  if (!sentAt) return null;
  const d = new Date(sentAt);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const isSameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

  if (isSameDay(d, today)) return 'Today';
  if (isSameDay(d, yesterday)) return 'Yesterday';
  return d.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });
};

const notifyUnreadCountChanged = () => {
  window.dispatchEvent(new CustomEvent('chat-unread-count-changed'));
};

const MemberChat = () => {
  const { pointsSystemEnabled } = useTier();
  const location = useLocation();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);

  const [selectedConversation, setSelectedConversation] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewChatDialog, setShowNewChatDialog] = useState(false);
  const [showQuoteDetailsDialog, setShowQuoteDetailsDialog] = useState(false);
  const [quoteDetailsLoading, setQuoteDetailsLoading] = useState(false);
  const [quoteDetails, setQuoteDetails] = useState(null);
  const [selectedMember, setSelectedMember] = useState('');
  const [loading, setLoading] = useState(true);
  const handledNavigationStateRef = useRef('');

  // Filters
  const [countryFilter, setCountryFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [memberSearchTerm, setMemberSearchTerm] = useState('');

  const { member } = useAuth();
  const { hasRestriction, getRestrictionMessage } = useTier();
  const currentMemberId = member?.member_id;
  const messagesEndRef = useRef(null);
  const isSelectedConversationChatClosed = Boolean(selectedConversation && selectedConversation.chat_active === false);
  const selectedConversationClosedMessage = selectedConversation?.chat_closed_message || 'Shipment is closed';

  const getQuoteInsuranceDisplay = useCallback((quote) => {
    const fd = quote?.form_data || {};
    const insuranceValue = fd.insuranceValue ?? fd.customsValue ?? fd.customsDetails?.customsValue;
    const hasInsuranceValue = insuranceValue !== '' && insuranceValue != null;

    if (!fd.insuranceRequired && !hasInsuranceValue) return '';

    if (!hasInsuranceValue) return 'Required';

    const numericValue = Number(insuranceValue);
    const displayValue = Number.isFinite(numericValue)
      ? numericValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : insuranceValue;

    return `${fd.insuranceRequired ? 'Required - ' : ''}Invoice Value (USD) ${displayValue}`;
  }, []);

  const getQuoteRouteText = useCallback((quote) => {
    if (!quote) return 'N/A';
    const fd = quote.form_data || {};

    if (quote.quote_type === 'Warehousing') {
      return fd.warehouseLocation || quote.origin_city || quote.origin_country || 'Warehouse location not specified';
    }

    if (quote.quote_type === 'Ocean FCL' || quote.quote_type === 'Ocean LCL') {
      const origin = quote.origin_port?.name || fd.polName || fd.pol || quote.origin_city || 'Origin not specified';
      const destination = quote.destination_port?.name || fd.podName || fd.pod || quote.destination_city || 'Destination not specified';
      return `${origin} -> ${destination}`;
    }

    if (quote.quote_type === 'Air') {
      const origin = fd.origin || quote.origin_city || 'Origin not specified';
      const destination = fd.destination || quote.destination_city || 'Destination not specified';
      return `${origin} -> ${destination}`;
    }

    if (quote.quote_type === 'Transport') {
      const pickup = fd.pickupLocation?.city || fd.pickupLocation?.address || quote.origin_city || 'Pickup not specified';
      const delivery = fd.deliveryLocation?.city || fd.deliveryLocation?.address || quote.destination_city || 'Delivery not specified';
      return `${pickup} -> ${delivery}`;
    }

    if (quote.quote_type === 'Breakbulk') {
      const origin = quote.origin_port?.name || fd.polName || fd.pol || quote.origin_city || 'Origin not specified';
      const destination = quote.destination_port?.name || fd.podName || fd.pod || quote.destination_city || 'Destination not specified';
      return `${origin} -> ${destination}`;
    }

    return 'N/A';
  }, []);

  const getStatusBadge = useCallback((status) => {
    const badges = {
      Draft: { className: 'bg-gray-100 text-gray-700', label: 'Draft' },
      Active: { className: 'bg-green-100 text-green-700', label: 'Active' },
      Pending: { className: 'bg-yellow-100 text-yellow-700', label: 'Pending' },
      Assigned: { className: 'bg-blue-100 text-blue-700', label: 'Assigned' },
      Nominated: { className: 'bg-purple-100 text-purple-700', label: 'Nominated' },
      Completed: { className: 'bg-emerald-100 text-emerald-700', label: 'Completed' },
      Cancelled: { className: 'bg-red-100 text-red-700', label: 'Cancelled' },
      Closed: { className: 'bg-gray-100 text-gray-700', label: 'Closed' }
    };
    return badges[status] || badges.Draft;
  }, []);

  const getPriorityBadge = useCallback((priority) => {
    const badges = {
      Normal: { className: 'bg-gray-100 text-gray-600', label: 'Normal' },
      Medium: { className: 'bg-amber-100 text-amber-700', label: 'Medium' },
      High: { className: 'bg-red-100 text-red-700', label: 'High' }
    };
    return badges[priority] || badges.Normal;
  }, []);

  const getMemberDisplayName = useCallback((memberInfo) => {
    return (
      memberInfo?.individual_name ||
      memberInfo?.contact_name ||
      memberInfo?.full_name ||
      memberInfo?.company_name ||
      'Member'
    );
  }, []);

  const calculateTotals = useCallback((items) => {
    let subtotal = 0;
    let taxAmount = 0;
    let atActuals = 0;

    (items || []).forEach((item) => {
      if (item.is_at_actuals) {
        atActuals += (item.unit_price || 0) * (item.quantity || 1);
      } else {
        const lineTotal = (item.unit_price || 0) * (item.quantity || 1);
        subtotal += lineTotal;
        if (item.tax_applicable && item.tax_rate) {
          taxAmount += lineTotal * (item.tax_rate / 100);
        }
      }
    });

    return {
      subtotal: subtotal.toFixed(2),
      taxAmount: taxAmount.toFixed(2),
      atActuals: atActuals.toFixed(2),
      total: (subtotal + taxAmount + atActuals).toFixed(2)
    };
  }, []);

  const getQuoteScopeDisplay = useCallback((quote) => {
    const scope = quote?.form_data?.scope || quote?.scope;
    if (!Array.isArray(scope) || scope.length === 0) return '—';
    return scope.join(', ');
  }, []);

  const getQuoteStandardContainerDisplay = useCallback((quote) => {
    const standardContainerTypes = ['20GP', '20HC', '40GP', '40HC', '45HC'];
    const containers = quote?.containers || quote?.form_data?.containers;
    if (!containers) return '—';

    const parts = [];
    if (Array.isArray(containers)) {
      standardContainerTypes.forEach((type) => {
        const item = containers.find((container) => container.container_type === type);
        const qty = item?.quantity ?? 0;
        if (qty > 0) parts.push(`${qty}x ${type}`);
      });
    } else {
      standardContainerTypes.forEach((type) => {
        const qty = containers[type] ?? 0;
        if (qty > 0) parts.push(`${qty}x ${type}`);
      });
    }

    return parts.length > 0 ? parts.join(', ') : '—';
  }, []);

  const getQuoteSpecialContainerDetails = useCallback((quote) => {
    const reefer = quote?.reefer || quote?.form_data?.reefer || {};
    const oog = quote?.oogContainers || quote?.form_data?.oogContainers || [];
    const details = [];

    Object.entries(reefer).forEach(([type, qty]) => {
      if (qty > 0) details.push(`${qty}x ${type}`);
    });

    if (Array.isArray(oog) && oog.length > 0) {
      oog.forEach((item, index) => {
        const dims = [item.length, item.width, item.height].filter(Boolean).join(' x ');
        details.push(
          `Special Container ${index + 1}: ${[
            item.type || 'OOG',
            item.gauge === 'out' ? 'Out-Gauge' : item.gauge === 'in' ? 'In-Gauge' : '',
            dims ? `${dims} ${item.unit || 'mm'}` : '',
            item.weight ? `${item.weight} kg` : '',
            item.cargoDescription || ''
          ].filter(Boolean).join(', ')}`
        );
      });
    }

    return details.length > 0 ? details : [];
  }, []);

  const getConversationRelatedBid = useCallback((quote, conversation) => {
    const bids = Array.isArray(quote?.bids) ? quote.bids : [];
    if (!bids.length || !conversation) return null;

    if (conversation.bid_id) {
      const bidById = bids.find((bid) => bid?.bid_id === conversation.bid_id);
      if (bidById) return bidById;
    }

    const isConversationWithRequester = conversation.memberId === quote?.requester_id;
    const targetBidderId = isConversationWithRequester ? currentMemberId : conversation.memberId;
    if (!targetBidderId) return null;

    const exactMatch = bids.find((bid) => bid?.bidder_id === targetBidderId);
    if (exactMatch) return exactMatch;

    if (quote?.my_bid_id) {
      const myBid = bids.find((bid) => bid?.bid_id === quote.my_bid_id);
      if (myBid) return myBid;
    }

    return null;
  }, [currentMemberId]);

  const openQuoteDetailsDialog = useCallback(async (conversation) => {
    if (!conversation?.quote_id) {
      toast.error('Quote details are not available for this conversation');
      return;
    }

    setShowQuoteDetailsDialog(true);
    setQuoteDetailsLoading(true);
    setQuoteDetails(null);

    try {
      const response = await quotesApi.getQuote(conversation.quote_id);
      setQuoteDetails(response.data || null);
    } catch (error) {
      console.error('Error loading quote details:', error);
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to load quote details'));
      setShowQuoteDetailsDialog(false);
    } finally {
      setQuoteDetailsLoading(false);
    }
  }, []);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const fetchConversations = useCallback(async () => {
    if (!currentMemberId) return;
    try {
      const response = await chatApi.getConversations(currentMemberId);
      const backendConvs = response.data || [];
      const mapped = await Promise.all(backendConvs.map(async (conv) => {
        const otherId = conv.participants?.find(p => p !== currentMemberId);
        let memberName = 'Unknown';
        let company = '';
        let country = '';
        let city = '';
        let tier = '';
        let member_rating = null;
        let companyLogo = null;
        let otherParticipantName = 'Unknown';
        if (otherId) {
          try {
            const memberRes = await directoryApi.getMember(otherId);
            const m = memberRes.data;
            if (m) {
              // Prefer member/person name over company name for chat display
              otherParticipantName = m.individual_name || m.contact_name || m.company_name || 'Unknown';
              memberName = otherParticipantName;
              company = m.company_name || '';
              country = m.country || '';
              city = m.city || '';
              tier = m.tier || '';
              member_rating = m.member_rating;
              companyLogo = m.company_logo;
            }
          } catch (e) {
            console.warn('Could not fetch member for conversation:', otherId);
          }
        }
        const unreadCount = conv.unread_count?.[currentMemberId] ?? 0;
        const isQuoteThread = conv.conversation_type === 'Quote';
        const refNum = conv.reference_number;
        const quoteTitle = isQuoteThread && (refNum || conv.title)
          ? (refNum ? `Quote Discussion - ${refNum}` : conv.title)
          : null;
        return {
          id: conv.conversation_id || conv.id,
          conversation_id: conv.conversation_id,
          quote_id: conv.quote_id,
          bid_id: conv.bid_id,
          conversation_type: conv.conversation_type,
          chat_active: conv.chat_active,
          chat_closed_message: conv.chat_closed_message,
          title: conv.title,
          reference_number: refNum,
          memberId: otherId,
          memberName: isQuoteThread ? (quoteTitle || memberName) : memberName,
          otherParticipantName,
          company,
          country,
          city,
          tier,
          rating: member_rating,
          companyLogo: isQuoteThread ? null : companyLogo,
          lastMessage: isQuoteThread ? '' : (conv.last_message || ''),
          time: conv.last_message_at ? formatTime(conv.last_message_at) : '',
          unreadCount,
          messages: []
        };
      }));
      setConversations(mapped);
    } catch (error) {
      console.error('Error fetching conversations:', error);
      setConversations([]);
    }
  }, [currentMemberId]);

  const formatTime = (iso) => {
    const d = new Date(iso);
    const now = new Date();
    const diff = now - d;
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)} min ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} hours ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)} days ago`;
    return d.toLocaleDateString();
  };

  useEffect(() => {
    fetchMembers();
  }, []);

  useEffect(() => {
    if (currentMemberId) fetchConversations();
  }, [currentMemberId, fetchConversations]);

  // Load message history and mark as read when selecting a conversation
  useEffect(() => {
    if (!selectedConversation?.conversation_id || !currentMemberId) return;
    let cancelled = false;
    (async () => {
      try {
        const [messagesRes] = await Promise.all([
          chatApi.getMessages(selectedConversation.conversation_id),
          chatApi.markAsRead(selectedConversation.conversation_id, currentMemberId)
        ]);
        const backendMessages = messagesRes.data || [];
        if (cancelled) return;
        const mapped = backendMessages.map((m) => mapBackendMessageToFrontend(m, currentMemberId));
        setConversations((prev) =>
          prev.map((conv) =>
            conv.conversation_id === selectedConversation.conversation_id
              ? { ...conv, messages: mapped, unreadCount: 0 }
              : conv
          )
        );
        setSelectedConversation((prev) =>
          prev?.conversation_id === selectedConversation.conversation_id ? { ...prev, messages: mapped, unreadCount: 0 } : prev
        );
        notifyUnreadCountChanged();
      } catch (err) {
        if (!cancelled) console.error('Error loading messages:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedConversation?.conversation_id, currentMemberId]);

  const handleNewMessage = useCallback(
    (payload) => {
      if (payload.type !== 'new_message' || !payload.conversation_id || !payload.message) return;
      const msg = payload.message;
      const frontendMsg = mapBackendMessageToFrontend(msg, currentMemberId);
      if (frontendMsg.sender === 'me') return; // We already added it optimistically

      const convId = payload.conversation_id;
      const content = msg.content || '';
      const isOtherConversation = selectedConversation?.conversation_id !== convId;

      // If user is viewing this conversation, mark as read so backend unread_count stays correct
      if (selectedConversation?.conversation_id === convId) {
        chatApi.markAsRead(convId, currentMemberId).catch(() => {});
      }

      setConversations((prev) => {
        const exists = prev.some((c) => c.conversation_id === convId);
        if (!exists) {
          fetchConversations();
          notifyUnreadCountChanged();
          return prev;
        }
        return prev.map((c) => {
          if (c.conversation_id !== convId) return c;
          const isNewMessage = !c.messages.some((m) => m.message_id === frontendMsg.message_id);
          const isViewing = selectedConversation?.conversation_id === convId;
          return {
            ...c,
            messages: isNewMessage ? [...c.messages, frontendMsg] : c.messages,
            lastMessage: content,
            time: 'Just now',
            unreadCount: isViewing ? 0 : (isNewMessage ? (c.unreadCount || 0) + 1 : (c.unreadCount || 0)),
          };
        });
      });

      setSelectedConversation((prev) => {
        if (!prev || prev.conversation_id !== convId) return prev;
        if (prev.messages.some((m) => m.message_id === frontendMsg.message_id)) return prev;
        return { ...prev, messages: [...prev.messages, frontendMsg], lastMessage: content, time: 'Just now' };
      });

      if (isOtherConversation) {
        const conv = conversations.find((c) => c.conversation_id === convId);
        const name = conv?.memberName || conv?.otherParticipantName || 'Someone';
        toast.info(`New message from ${name}`);
      }

      notifyUnreadCountChanged();
    },
    [currentMemberId, conversations, fetchConversations, selectedConversation?.conversation_id]
  );

  const handleMessagesRead = useCallback((payload) => {
    if (payload.type !== 'messages_read' || !payload.conversation_id || !payload.reader_id) return;
    const convId = payload.conversation_id;
    const readerId = payload.reader_id;
    setConversations((prev) =>
      prev.map((c) =>
        c.conversation_id === convId
          ? {
              ...c,
              messages: c.messages.map((m) =>
                m.sender === 'me' && !(m.read_by || []).includes(readerId)
                  ? { ...m, read_by: [...(m.read_by || []), readerId] }
                  : m
              ),
            }
          : c
      )
    );
    setSelectedConversation((prev) =>
      prev?.conversation_id === convId
        ? {
            ...prev,
            messages: prev.messages.map((m) =>
              m.sender === 'me' && !(m.read_by || []).includes(readerId)
                ? { ...m, read_by: [...(m.read_by || []), readerId] }
                : m
            ),
          }
        : prev
    );
  }, []);

  const { registerHandlers } = useChatWebSocketContext() || {};
  useEffect(() => {
    if (!registerHandlers || !currentMemberId) return;
    const unregister = registerHandlers(handleNewMessage, handleMessagesRead);
    return unregister;
  }, [registerHandlers, currentMemberId, handleNewMessage, handleMessagesRead]);

  // Refetch messages when tab becomes visible (fallback for missed WebSocket events)
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && selectedConversation?.conversation_id && currentMemberId) {
        (async () => {
          try {
            const messagesRes = await chatApi.getMessages(selectedConversation.conversation_id);
            const backendMessages = messagesRes.data || [];
            const mapped = backendMessages.map((m) => mapBackendMessageToFrontend(m, currentMemberId));
            setConversations((prev) =>
              prev.map((conv) =>
                conv.conversation_id === selectedConversation.conversation_id
                  ? { ...conv, messages: mapped }
                  : conv
              )
            );
            setSelectedConversation((prev) =>
              prev?.conversation_id === selectedConversation.conversation_id ? { ...prev, messages: mapped } : prev
            );
          } catch (err) {
            console.error('Error refetching messages on visibility:', err);
          }
        })();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [selectedConversation?.conversation_id, currentMemberId]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [selectedConversation?.messages, scrollToBottom]);

  // Build local conversation object from backend quote conversation (companyName = other participant's company; otherParticipantName = their person name)
  const mapQuoteConvToLocal = useCallback((backendConv, companyName, quoteReference = null, otherParticipantName = null) => {
    const otherId = backendConv.participants?.find(p => p !== currentMemberId);
    const ref = quoteReference || backendConv.reference_number;
    const displayTitle = ref ? `Quote Discussion - ${ref}` : (backendConv.title || 'Quote Discussion');
    return {
      id: backendConv.conversation_id,
      conversation_id: backendConv.conversation_id,
      quote_id: backendConv.quote_id,
      bid_id: backendConv.bid_id,
      conversation_type: 'Quote',
      chat_active: backendConv.chat_active,
      chat_closed_message: backendConv.chat_closed_message,
      title: displayTitle,
      reference_number: ref || backendConv.reference_number,
      memberId: otherId,
      memberName: displayTitle,
      otherParticipantName: otherParticipantName || companyName,
      company: companyName,
      country: '',
      city: '',
      tier: '',
      rating: null,
      companyLogo: null,
      lastMessage: '', // Quote threads: do not show previous message snippet
      time: backendConv.last_message_at ? formatTime(backendConv.last_message_at) : 'Just now',
      unreadCount: 0,
      messages: []
    };
  }, [currentMemberId]);

  // Open or create a quote-specific chat thread
  const openOrCreateQuoteChat = useCallback(async (openQuoteChat) => {
    const { quote_id, quote_reference, bid_id, member_id, company_name, other_participant_name } = openQuoteChat;
    if (!currentMemberId) {
      toast.error('Please sign in to open the quote chat');
      return;
    }
    try {
      let data;
      if (bid_id) {
        const res = await chatApi.openBidQuoteChat(quote_id, bid_id);
        data = res.data || {};
      } else {
        const res = await chatApi.getQuoteChat(quote_id, currentMemberId, member_id);
        data = res.data || {};
      }
      let localConv;

      if (data.conversation_id) {
        const existing = conversations.find(
          c => c.conversation_id === data.conversation_id ||
            (
              c.quote_id === quote_id &&
              c.conversation_type === 'Quote' &&
              c.memberId === member_id
            )
        );
        if (existing) {
          setSelectedConversation(existing);
          toast.info(`Opened quote thread: ${quote_reference || quote_id}`);
          return;
        }
        localConv = mapQuoteConvToLocal(data, company_name, quote_reference, other_participant_name);
        setConversations(prev => [localConv, ...prev]);
        toast.info(`Opened quote thread: ${quote_reference || quote_id}`);
      } else {
        const createRes = await chatApi.createQuoteChat(quote_id, [currentMemberId, member_id], quote_reference);
        const created = createRes.data || {};
        localConv = mapQuoteConvToLocal(created, company_name, quote_reference, other_participant_name);
        setConversations(prev => [localConv, ...prev]);
        toast.success(`Started quote thread for ${quote_reference || quote_id}`);
      }

      setSelectedConversation(localConv);
    } catch (error) {
      console.error('Error opening quote chat:', error);
      toast.error(getErrorMessage(error.response?.data?.detail, 'Could not open quote chat'));
    }
  }, [currentMemberId, conversations, mapQuoteConvToLocal]);

  // Handle incoming navigation state (from Directory "Send Message", Quote Tool, or notification link)
  useEffect(() => {
    const navState = location.state || {};
    const queryConversationId = new URLSearchParams(location.search).get('conversation_id');
    if (queryConversationId) {
      const stateKey = `conversation:${queryConversationId}`;
      const conv = conversations.find((c) => c.conversation_id === queryConversationId);
      if (conv && handledNavigationStateRef.current !== stateKey) {
        handledNavigationStateRef.current = stateKey;
        setSelectedConversation(conv);
        navigate(location.pathname, { replace: true });
      }
      return;
    }
    if (location.state?.openQuoteChat) {
      const stateKey = `quote:${JSON.stringify(navState.openQuoteChat)}`;
      if (handledNavigationStateRef.current === stateKey) return;
      handledNavigationStateRef.current = stateKey;
      const openQuoteChat = location.state.openQuoteChat;
      openOrCreateQuoteChat(openQuoteChat);
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }
    if (location.state?.openChatWith) {
      const stateKey = `direct:${JSON.stringify(navState.openChatWith)}`;
      if (handledNavigationStateRef.current === stateKey) return;
      handledNavigationStateRef.current = stateKey;
      const memberToChat = location.state.openChatWith;
      openOrCreateConversation(memberToChat);
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }
    if (location.state?.openConversationId) {
      const stateKey = `conversation:${navState.openConversationId}`;
      if (handledNavigationStateRef.current === stateKey) return;
      const wantId = location.state.openConversationId;
      const conv = conversations.find((c) => c.conversation_id === wantId);
      if (conv) {
        handledNavigationStateRef.current = stateKey;
        setSelectedConversation(conv);
        navigate(location.pathname, { replace: true, state: {} });
      }
      return;
    }
    handledNavigationStateRef.current = '';
  }, [location.state, location.search, location.pathname, conversations, navigate, openOrCreateQuoteChat]);

  const openOrCreateConversation = async (memberInfo) => {
    if (!currentMemberId) {
      toast.error('Please sign in to start a conversation');
      return;
    }
    // Check if conversation already exists locally
    const existingConv = conversations.find(
      conv => conv.memberId === memberInfo.member_id
    );

    const personName = getMemberDisplayName(memberInfo);
    if (existingConv) {
      setSelectedConversation(existingConv);
      toast.info(`Continuing conversation with ${personName}`);
    } else {
      // Create conversation via backend API
      try {
        const response = await chatApi.createConversation({
          conversation_type: 'Direct',
          participants: [currentMemberId, memberInfo.member_id],
          title: personName || memberInfo.company_name
        });

        const backendConv = response.data;

        // Create local conversation state with backend ID (prefer member name over company)
        const newConv = {
          id: backendConv.conversation_id || `conv-${Date.now()}`,
          conversation_id: backendConv.conversation_id,
          memberId: memberInfo.member_id,
          memberName: personName,
          otherParticipantName: personName,
          company: memberInfo.company_name,
          country: memberInfo.country,
          city: memberInfo.city,
          tier: memberInfo.tier,
          rating: memberInfo.member_rating,
          companyLogo: memberInfo.company_logo,
          lastMessage: '',
          time: 'Just now',
          unreadCount: 0,
          messages: []
        };

        setConversations(prev => [newConv, ...prev]);
        setSelectedConversation(newConv);
        toast.success(`Started new conversation with ${personName}`);
      } catch (error) {
        console.error('Error creating conversation:', error);
        // Fallback to local-only conversation if API fails
        const newConv = {
          id: `conv-${Date.now()}`,
          memberId: memberInfo.member_id,
          memberName: personName,
          otherParticipantName: personName,
          company: memberInfo.company_name,
          country: memberInfo.country,
          city: memberInfo.city,
          tier: memberInfo.tier,
          rating: memberInfo.member_rating,
          companyLogo: memberInfo.company_logo,
          lastMessage: '',
          time: 'Just now',
          unreadCount: 0,
          messages: []
        };

        setConversations(prev => [newConv, ...prev]);
        setSelectedConversation(newConv);
        toast.warning(`Could not create conversation. Please try again.`);
      }
    }
  };

  useEffect(() => {
    fetchMembers();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [members, countryFilter, cityFilter, memberSearchTerm]);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const response = await directoryApi.getMembers({ limit: 200 });
      const allMembers = response.data;

      // Sort by favorites first, then by rating (highest first)
      const sortedMembers = allMembers.sort((a, b) => {
        // Favorites first
        if (a.is_favorite && !b.is_favorite) return -1;
        if (!a.is_favorite && b.is_favorite) return 1;
        // Then by rating
        return (b.member_rating || 0) - (a.member_rating || 0);
      });

      setMembers(sortedMembers);

      // Extract unique countries and cities
      const uniqueCountries = [...new Set(sortedMembers.map(m => m.country).filter(Boolean))].sort();
      const uniqueCities = [...new Set(sortedMembers.map(m => m.city).filter(Boolean))].sort();
      setCountries(uniqueCountries);
      setCities(uniqueCities);
    } catch (error) {
      console.error('Error fetching members:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let result = [...members];

    // Filter by country
    if (countryFilter) {
      result = result.filter(m => m.country === countryFilter);
    }

    // Filter by city
    if (cityFilter) {
      result = result.filter(m => m.city === cityFilter);
    }

    // Filter by search term
    if (memberSearchTerm) {
      const term = memberSearchTerm.toLowerCase();
      result = result.filter(m =>
        (m.company_name || '').toLowerCase().includes(term) ||
        (m.individual_name || '').toLowerCase().includes(term) ||
        (m.contact_name || '').toLowerCase().includes(term) ||
        (m.full_name || '').toLowerCase().includes(term) ||
        (m.city || '').toLowerCase().includes(term)
      );
    }

    // Exclude current user
    result = result.filter(m => m.member_id !== currentMemberId);

    setFilteredMembers(result);

    // Update cities based on selected country
    if (countryFilter) {
      const citiesInCountry = [...new Set(
        members.filter(m => m.country === countryFilter).map(m => m.city).filter(Boolean)
      )].sort();
      setCities(citiesInCountry);
    } else {
      setCities([...new Set(members.map(m => m.city).filter(Boolean))].sort());
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split('.').pop()?.toLowerCase();
    const allowed = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'pdf', 'doc', 'docx', 'xls', 'xlsx'];
    if (!ext || !allowed.includes(ext)) {
      toast.error('Allowed: images (png, jpg, gif, webp), PDF, Word, Excel');
      return;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      toast.error(`File too large. Max ${MAX_FILE_SIZE_MB}MB.`);
      return;
    }
    setAttachment(file);
    e.target.value = '';
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    const hasMessageContent = Boolean(newMessage.trim());
    if ((!hasMessageContent && !attachment) || !selectedConversation || isSelectedConversationChatClosed) return;

    let fileUrl = null;
    let messageType = 'Text';
    // Validate with trim(), but send the original text so pasted indentation,
    // paragraphs, and leading/trailing line breaks remain intact.
    let content = hasMessageContent ? newMessage : '';

    if (attachment) {
      setUploading(true);
      try {
        const res = await uploadsApi.uploadFile(attachment, 'chat', null, currentMemberId);
        fileUrl = res.data?.file?.url || res.data?.file_url;
        const ext = attachment.name.split('.').pop()?.toLowerCase();
        messageType = IMAGE_EXTENSIONS.has(ext) ? 'Image' : 'File';
        content = content || attachment.name;
      } catch (err) {
        console.error('Upload failed:', err);
        toast.error('Failed to upload file. Please try again.');
        setUploading(false);
        return;
      }
      setUploading(false);
      setAttachment(null);
    }

    const now = new Date();
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const newMsg = {
      message_id: tempId,
      sender: 'me',
      text: content,
      time: now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      sent_at: now.toISOString(),
      message_type: messageType,
      file_url: fileUrl,
      read_by: [currentMemberId], // Sender has "read" it; recipient hasn't yet
    };

    setNewMessage('');

    setConversations(prev => prev.map(conv => {
      if (conv.id === selectedConversation.id) {
        return {
          ...conv,
          messages: [...conv.messages, newMsg],
          lastMessage: content,
          time: 'Just now'
        };
      }
      return conv;
    }));

    setSelectedConversation(prev => ({
      ...prev,
      messages: [...prev.messages, newMsg],
      lastMessage: content
    }));

    try {
      let conversationId = selectedConversation.conversation_id;

      if (!conversationId) {
        const createResponse = await chatApi.createConversation({
          conversation_type: 'Direct',
          participants: [currentMemberId, selectedConversation.memberId],
          title: selectedConversation.memberName
        });
        conversationId = createResponse.data.conversation_id;

        setConversations(prev => prev.map(conv => {
          if (conv.id === selectedConversation.id) {
            return { ...conv, conversation_id: conversationId };
          }
          return conv;
        }));

        setSelectedConversation(prev => ({
          ...prev,
          conversation_id: conversationId
        }));
      }

      const sendRes = await chatApi.sendMessage(conversationId, {
        sender_id: currentMemberId,
        content,
        message_type: messageType,
        file_url: fileUrl || undefined
      });
      const backendMsg = sendRes.data;
      if (backendMsg?.message_id) {
        const updatedMsg = mapBackendMessageToFrontend(backendMsg, currentMemberId);
        setConversations(prev => prev.map(conv =>
          conv.conversation_id === conversationId
            ? { ...conv, messages: conv.messages.map(m => m.message_id === tempId ? updatedMsg : m) }
            : conv
        ));
        setSelectedConversation(prev =>
          prev?.conversation_id === conversationId
            ? { ...prev, messages: prev.messages.map(m => m.message_id === tempId ? updatedMsg : m) }
            : prev
        );
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const msg = getErrorMessage(error.response?.data?.detail, 'Failed to send message. Please try again.');
      toast.error(msg);
    }
  };

  const handleStartNewChat = async () => {
    if (!selectedMember) return;
    const memberData = members.find(m => m.member_id === selectedMember);
    if (memberData) {
      openOrCreateConversation({
        member_id: memberData.member_id,
        company_name: memberData.company_name,
        individual_name: memberData.individual_name,
        country: memberData.country,
        city: memberData.city,
        tier: memberData.tier,
        member_rating: memberData.member_rating
      });
      setShowNewChatDialog(false);
      setSelectedMember('');
      resetFilters();
    }
  };

  const resetFilters = () => {
    setCountryFilter('');
    setCityFilter('');
    setMemberSearchTerm('');
  };

  const filteredConversations = conversations.filter(conv =>
    conv.memberName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const renderStars = (rating) => {
    return (
      <div className="flex items-center space-x-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={12}
            className={star <= Math.round(rating || 0) ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}
          />
        ))}
        <span className="text-xs text-gray-500 ml-1">({(rating || 0).toFixed(1)})</span>
      </div>
    );
  };

  if (hasRestriction('chat')) {
    return <RestrictedOverlay message={getRestrictionMessage('chat')} />;
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex-shrink-0 flex items-center justify-between py-2 px-4 bg-white">
        <div>
          <h1 className="text-lg font-bold text-gray-900">Messages</h1>
          <p className="text-gray-500 text-xs">Connect with members across the network</p>
        </div>
        <Dialog open={showNewChatDialog} onOpenChange={(open) => {
          setShowNewChatDialog(open);
          if (!open) resetFilters();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="new-message-btn">
              <MessageSquare size={18} className="mr-2" />
              New Message
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle>Start New Conversation</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4 flex-1 overflow-hidden flex flex-col">
              {/* Filters */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 shrink-0 p-1">
                <div>
                  <Label className="text-xs text-gray-500">Country</Label>
                  <select
                    className="w-full min-w-0 pl-3 pr-8 py-2 border rounded-md text-sm bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                    value={countryFilter}
                    onChange={(e) => {
                      setCountryFilter(e.target.value);
                      setCityFilter('');
                    }}
                    data-testid="country-filter"
                  >
                    <option value="">All Countries</option>
                    {countries.map(country => (
                      <option key={country} value={country}>{country}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">City</Label>
                  <select
                    className="w-full min-w-0 pl-3 pr-8 py-2 border rounded-md text-sm bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                    value={cityFilter}
                    onChange={(e) => setCityFilter(e.target.value)}
                    data-testid="city-filter"
                  >
                    <option value="">All Cities</option>
                    {cities.map(city => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Search</Label>
                  <div className="relative">
                    <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                    <Input
                      placeholder="Search members..."
                      value={memberSearchTerm}
                      onChange={(e) => setMemberSearchTerm(e.target.value)}
                      className="pl-8 h-9"
                    />
                  </div>
                </div>
              </div>

              {/* Active Filters */}
              {(countryFilter || cityFilter) && (
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-gray-500">Active filters:</span>
                  {countryFilter && (
                    <Badge variant="outline" className="text-xs">
                      {countryFilter}
                      <X
                        size={12}
                        className="ml-1 cursor-pointer"
                        onClick={() => setCountryFilter('')}
                      />
                    </Badge>
                  )}
                  {cityFilter && (
                    <Badge variant="outline" className="text-xs">
                      {cityFilter}
                      <X
                        size={12}
                        className="ml-1 cursor-pointer"
                        onClick={() => setCityFilter('')}
                      />
                    </Badge>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-xs"
                    onClick={resetFilters}
                  >
                    Clear all
                  </Button>
                </div>
              )}

              {/* Member List */}
              <div className="flex-1 overflow-y-auto border rounded-lg">
                <div className="p-2 text-xs text-gray-500 bg-gray-50 border-b sticky top-0">
                  <Star size={12} className="inline text-amber-400 fill-amber-400 mr-1" />
                  Showing favorites and highest-rated members first ({filteredMembers.length} members)
                </div>

                {loading ? (
                  <div className="p-8 text-center text-gray-500">Loading members...</div>
                ) : filteredMembers.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">No members found with selected filters</div>
                ) : (
                  <div className="divide-y">
                    {filteredMembers.map(member => (
                      (() => {
                        const displayName = getMemberDisplayName(member);
                        const companyLabel =
                          member.company_name && member.company_name !== displayName
                            ? member.company_name
                            : null;
                        return (
                      <div
                        key={member.member_id}
                        className={`p-3 cursor-pointer hover:bg-blue-50 transition-colors ${selectedMember === member.member_id ? 'bg-blue-100 border-l-4 border-l-blue-500' : ''
                          }`}
                        onClick={() => setSelectedMember(member.member_id)}
                        data-testid={`member-item-${member.member_id}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <span className="font-medium text-sm truncate">
                                {displayName}
                              </span>
                              {member.is_favorite && (
                                <Star size={14} className="text-amber-400 fill-amber-400 flex-shrink-0" />
                              )}
                            </div>
                            {companyLabel && (
                              <p className="text-xs text-gray-500 mt-0.5 truncate">
                                {companyLabel}
                              </p>
                            )}
                            <div className="flex items-center space-x-2 mt-1">
                              <MapPin size={12} className="text-gray-400" />
                              <span className="text-xs text-gray-600">{member.city}, {member.country}</span>
                            </div>
                            {renderStars(member.member_rating)}
                          </div>
                          <Badge className="text-xs" variant="outline">{member.tier}</Badge>
                        </div>
                      </div>
                        );
                      })()
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 pt-2 border-t">
                <Button variant="outline" onClick={() => {
                  setShowNewChatDialog(false);
                  resetFilters();
                }}>
                  Cancel
                </Button>
                <Button
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  onClick={handleStartNewChat}
                  disabled={!selectedMember}
                  data-testid="start-chat-btn"
                >
                  <MessageSquare size={14} className="mr-1" />
                  Start Chat
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-3 gap-4 px-4 pb-4 overflow-hidden">
        {/* Conversations List */}
        <Card className={`lg:col-span-1 flex flex-col min-h-0 overflow-hidden ${selectedConversation ? 'hidden lg:flex' : ''}`}>
          <CardHeader className="flex-shrink-0 py-3 px-4">
            <div className="flex items-center space-x-2">
              <Search size={18} className="text-gray-400" />
              <Input
                placeholder="Search conversations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border-0 focus-visible:ring-0 shadow-none"
              />
            </div>
          </CardHeader>
          <CardContent className="p-0 flex-1 min-h-0 overflow-y-auto">
            {filteredConversations.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                No conversations yet. Start a new chat!
              </div>
            ) : (
              filteredConversations.map((conv) => {
                const isQuote = conv.conversation_type === 'Quote' || conv.quote_id;
                const listDisplayName = isQuote ? (conv.memberName || conv.title || 'Quote Discussion') : conv.memberName;
                return (
                <div
                  key={conv.id}
                  onClick={() => setSelectedConversation(conv)}
                  className={`p-4 border-b cursor-pointer hover:bg-gray-50 transition-colors ${selectedConversation?.id === conv.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                    }`}
                >
                  <div className="flex items-start gap-3">
                    {isQuote ? (
                      <div className="w-10 h-10 rounded-full flex-shrink-0 bg-gray-200 flex items-center justify-center text-gray-600 font-semibold text-sm" aria-label="Quote discussion">
                        QD
                      </div>
                    ) : (
                      <img
                        src={getLogoOrInitials({ company_logo: conv.companyLogo }, listDisplayName)}
                        alt={listDisplayName}
                        className="w-10 h-10 rounded-full flex-shrink-0 object-cover"
                        onError={(e) => { e.target.src = `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(listDisplayName)}`; }}
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium truncate">{listDisplayName}</span>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {conv.unreadCount > 0 && (
                            <span className="bg-blue-600 text-white text-xs font-medium min-w-[18px] h-[18px] px-1.5 rounded-full flex items-center justify-center">
                              {conv.unreadCount > 99 ? '99+' : conv.unreadCount}
                            </span>
                          )}
                          <span className="text-xs text-gray-400">{conv.time}</span>
                        </div>
                      </div>
                      {isQuote && (conv.otherParticipantName || conv.company) ? (
                        <p className="text-xs text-gray-500 truncate mt-0.5">{conv.otherParticipantName || conv.company}</p>
                      ) : null}
                      <p className="text-sm text-gray-600 truncate mt-0.5">{conv.lastMessage}</p>
                    </div>
                  </div>
                </div>
              ); })
            )}
          </CardContent>
        </Card>

        {/* Chat Window */}
        <Card className={`lg:col-span-2 flex flex-col min-h-0 overflow-hidden ${selectedConversation ? '' : 'hidden lg:flex'}`}>
          {selectedConversation ? (
            (() => {
              const isQuoteThread = selectedConversation.conversation_type === 'Quote';
              const displayName = isQuoteThread
                ? (selectedConversation.title || selectedConversation.memberName || 'Quote Discussion')
                : selectedConversation.memberName;
              return (
            <>
              <CardHeader className="border-b py-3 px-4 flex-shrink-0 bg-white">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 flex-shrink-0 lg:hidden"
                      aria-label="Back to conversations"
                      onClick={() => setSelectedConversation(null)}
                    >
                      <ArrowLeft size={18} />
                    </Button>
                    {isQuoteThread ? (
                      <div className="w-9 h-9 rounded-full flex-shrink-0 bg-gray-200 flex items-center justify-center text-gray-600 font-semibold text-xs" aria-label="Quote discussion">
                        QD
                      </div>
                    ) : (
                      <img
                        src={getLogoOrInitials({ company_logo: selectedConversation.companyLogo }, displayName)}
                        alt={displayName}
                        className="w-9 h-9 rounded-full flex-shrink-0 object-cover"
                        onError={(e) => { e.target.src = `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(displayName)}`; }}
                      />
                    )}
                    <div className="min-w-0">
                      {isQuoteThread ? (
                        <CardTitle className="min-w-0 text-base">
                          <button
                            type="button"
                            onClick={() => openQuoteDetailsDialog(selectedConversation)}
                            className="block max-w-full truncate text-left hover:text-blue-700 hover:underline underline-offset-2"
                          >
                            {displayName}
                          </button>
                        </CardTitle>
                      ) : (
                        <CardTitle className="truncate text-base">{displayName}</CardTitle>
                      )}
                      {isQuoteThread && (selectedConversation.otherParticipantName || selectedConversation.company) ? (
                        <p className="text-xs text-gray-500 truncate mt-0.5">{selectedConversation.otherParticipantName || selectedConversation.company}</p>
                      ) : null}
                      {!isQuoteThread && selectedConversation.company && selectedConversation.company !== selectedConversation.memberName && (
                        <p className="text-xs text-gray-500 truncate">{selectedConversation.company}</p>
                      )}
                    </div>
                  </div>
                  {!isQuoteThread && selectedConversation.country && (
                    <Badge variant="outline" className="flex-shrink-0 text-xs py-0.5">{selectedConversation.country}</Badge>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs"
                    onClick={() => {
                      if (!selectedConversation?.messages?.length) {
                        toast.info('No messages to export.');
                        return;
                      }
                      const lines = [];
                      let currentDate = '';
                      selectedConversation.messages.forEach((msg) => {
                        const d = msg.sent_at ? new Date(msg.sent_at) : new Date();
                        const dateStr = d.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });
                        if (dateStr !== currentDate) {
                          currentDate = dateStr;
                          lines.push(`--- ${dateStr} ---`);
                        }
                        const sender = msg.sender === 'me'
                          ? (member?.individual_name || member?.company_name || 'You')
                          : (selectedConversation.otherParticipantName || selectedConversation.memberName || selectedConversation.company || 'Member');
                        lines.push(`${sender} (${msg.time}):`);
                        lines.push(msg.text || '');
                        if (msg.file_url) {
                          lines.push(`[Attachment: ${msg.text || 'File'}]`);
                        }
                        lines.push('');
                      });
                      const subject = `Chat Export - ${selectedConversation.memberName || 'Conversation'}`;
                      const body = lines.join('\n');
                      window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, '_blank');
                    }}
                    title="Export chat to email"
                  >
                    <Mail size={14} className="mr-1" />
                    Export
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs"
                    onClick={async () => {
                      if (!selectedConversation?.messages?.length) {
                        toast.info('No messages to copy.');
                        return;
                      }
                      const lines = [];
                      const conversationTitle = selectedConversation.memberName
                        ? `${selectedConversation.memberName}${selectedConversation.company ? ' - ' + selectedConversation.company : ''}`
                        : (selectedConversation.otherParticipantName || 'Conversation');
                      lines.push(`Chat with: ${conversationTitle}`);
                      lines.push('');
                      let currentDate = '';
                      selectedConversation.messages.forEach((msg) => {
                        const d = msg.sent_at ? new Date(msg.sent_at) : new Date();
                        const dateStr = d.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });
                        if (dateStr !== currentDate) {
                          currentDate = dateStr;
                          lines.push(`--- ${dateStr} ---`);
                        }
                        const sender = msg.sender === 'me'
                          ? (member?.individual_name || member?.company_name || 'You')
                          : (selectedConversation.otherParticipantName || selectedConversation.memberName || selectedConversation.company || 'Member');
                        lines.push(`${sender} (${msg.time}):`);
                        lines.push(msg.text || '');
                        if (msg.file_url) {
                          lines.push(`[Attachment: ${msg.text || 'File'}]`);
                        }
                        lines.push('');
                      });
                      const text = lines.join('\n');
                      try {
                        await navigator.clipboard.writeText(text);
                        toast.success('Chat copied to clipboard. Paste into your email.');
                      } catch (err) {
                        // Fallback for browsers without clipboard API
                        const textarea = document.createElement('textarea');
                        textarea.value = text;
                        textarea.style.position = 'fixed';
                        textarea.style.left = '-9999px';
                        document.body.appendChild(textarea);
                        textarea.focus();
                        textarea.select();
                        try {
                          document.execCommand('copy');
                          toast.success('Chat copied to clipboard. Paste into your email.');
                        } catch {
                          toast.error('Failed to copy. Please try again.');
                        }
                        document.body.removeChild(textarea);
                      }
                    }}
                    title="Copy chat to clipboard"
                  >
                    <Copy size={14} className="mr-1" />
                    Copy
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-4 flex-1 flex flex-col min-h-0 overflow-hidden">
                <div className="flex-1 min-h-0 overflow-y-auto mb-4 space-y-4 bg-gray-200 rounded-lg p-3">
                  {selectedConversation.messages.map((msg, idx) => {
                    const prevMsg = idx > 0 ? selectedConversation.messages[idx - 1] : null;
                    const prevDateLabel = prevMsg?.sent_at ? getDateLabel(prevMsg.sent_at) : null;
                    const currDateLabel = msg.sent_at ? getDateLabel(msg.sent_at) : null;
                    const showDateSeparator = !prevDateLabel || prevDateLabel !== currDateLabel;

                    return (
                      <React.Fragment key={msg.message_id || `msg-${idx}`}>
                        {showDateSeparator && currDateLabel && (
                          <div className="flex justify-center py-2">
                            <span className="text-xs text-gray-600 bg-white/80 px-3 py-1 rounded-full shadow-sm">
                              {currDateLabel}
                            </span>
                          </div>
                        )}
                        <div
                          className={`flex ${msg.sender === 'me' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg shadow-sm ${msg.sender === 'me'
                                ? 'bg-[#DCF8C6] text-gray-900'
                                : 'bg-white text-gray-900'
                              }`}
                          >
                            <p className={`text-xs font-medium mb-1 ${msg.sender === 'me' ? 'text-teal-700' : 'text-teal-600'}`}>
                              {msg.sender === 'me'
                                ? (member?.individual_name || member?.company_name || 'You')
                                : (selectedConversation.otherParticipantName || selectedConversation.memberName || selectedConversation.company) || 'Unknown'}
                            </p>
                            {msg.message_type === 'Image' && msg.file_url && (
                              <a
                                href={getFullFileUrl(msg.file_url)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block mb-2"
                              >
                                <img
                                  src={getFullFileUrl(msg.file_url)}
                                  alt="Shared image"
                                  className="max-w-full max-h-48 rounded object-cover"
                                />
                              </a>
                            )}
                            {msg.message_type === 'File' && msg.file_url && (
                              <a
                                href={getFullFileUrl(msg.file_url)}
                                target="_blank"
                                rel="noopener noreferrer"
                                download
                                className="flex items-center gap-2 text-sm mb-2 hover:underline"
                              >
                                <FileText size={16} />
                                <span className="truncate">{msg.text || 'Download file'}</span>
                                <Download size={14} className="flex-shrink-0" />
                              </a>
                            )}
                            {msg.text && !(msg.message_type === 'File' && msg.file_url) && (
                              <p className={`text-sm whitespace-pre-wrap break-words ${msg.message_type === 'Image' && msg.file_url ? 'mt-1' : ''}`}>
                                {msg.text}
                              </p>
                            )}
                            <div className={`flex items-center gap-1 mt-1 ${msg.sender === 'me' ? 'justify-end' : 'justify-start'}`}>
                              <p className={`text-xs ${msg.sender === 'me' ? 'text-gray-600' : 'text-gray-500'}`}>
                                {msg.time}
                              </p>
                              {msg.sender === 'me' && (
                                <span className="flex-shrink-0" title={msg.read_by?.includes(selectedConversation?.memberId) ? 'Read' : 'Sent'}>
                                  {msg.read_by?.includes(selectedConversation?.memberId) ? (
                                    <CheckCheck size={14} className="text-blue-600" aria-label="Read" />
                                  ) : (
                                    <Check size={14} className="text-gray-600" aria-label="Sent" />
                                  )}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
                {isSelectedConversationChatClosed ? (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                    {selectedConversationClosedMessage}
                  </div>
                ) : (
                  <form onSubmit={handleSendMessage} className="space-y-2">
                    {attachment && (
                      <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-100 rounded-lg px-3 py-2">
                        <FileText size={16} />
                        <span className="flex-1 truncate">{attachment.name}</span>
                        <button
                          type="button"
                          onClick={() => setAttachment(null)}
                          className="text-gray-500 hover:text-red-600"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    )}
                    <div className="flex items-end space-x-2">
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept={ALLOWED_CHAT_FILES}
                        onChange={handleFileSelect}
                        className="hidden"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        title="Attach image, PDF, or Excel"
                      >
                        <Paperclip size={18} />
                      </Button>
                      <Textarea
                        placeholder="Type a message..."
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        rows={2}
                        className="flex-1 min-h-[44px] max-h-32 resize-y"
                      />
                      <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white" disabled={uploading}>
                        <Send size={18} />
                      </Button>
                    </div>
                  </form>
                )}
              </CardContent>
            </>
              );
            })()
          ) : (
            <CardContent className="flex-1 flex items-center justify-center min-h-0">
              <div className="text-center">
                <MessageSquare size={48} className="mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">Select a conversation or start a new one</p>
              </div>
            </CardContent>
          )}
        </Card>
      </div>

      <Dialog
        open={showQuoteDetailsDialog}
        onOpenChange={(open) => {
          setShowQuoteDetailsDialog(open);
          if (!open) {
            setQuoteDetails(null);
            setQuoteDetailsLoading(false);
          }
        }}
      >
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between gap-3 pr-8">
              <span className="flex items-center">
                <Eye size={18} className="mr-2 text-blue-600" />
                Quote Details
              </span>
              {quoteDetails?.reference_number && (
                <span className="font-mono text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                  {quoteDetails.reference_number}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>

          {quoteDetailsLoading ? (
            <div className="flex items-center justify-center py-12 text-gray-600">
              <Loader2 size={22} className="mr-2 animate-spin" />
              Loading quote details...
            </div>
          ) : quoteDetails ? (
            <div className="space-y-6">
              <div className="flex flex-wrap items-center gap-2">
                <Badge className={getStatusBadge(quoteDetails.status).className}>{getStatusBadge(quoteDetails.status).label}</Badge>
                {pointsSystemEnabled && (
                  <Badge className={getPriorityBadge(quoteDetails.priority).className}>{getPriorityBadge(quoteDetails.priority).label} Priority</Badge>
                )}
                {pointsSystemEnabled && quoteDetails.is_promoted && (
                  <Badge className="bg-amber-100 text-amber-700">
                    <ArrowUp size={12} className="mr-1" />
                    Promoted
                  </Badge>
                )}
                {quoteDetails.has_submitted_bid && (
                  <Badge className="bg-green-100 text-green-700">
                    <Check size={12} className="mr-1" />
                    Bid Submitted
                  </Badge>
                )}
                {quoteDetails.my_bid_nominated && (
                  <Badge className="bg-purple-100 text-purple-700">
                    <CheckCircle size={12} className="mr-1" />
                    Your Bid Nominated
                  </Badge>
                )}
                <span className="ml-auto flex items-center text-sm text-gray-500">
                  <Clock size={14} className="mr-1" />
                  Created: {quoteDetails.created_at ? new Date(quoteDetails.created_at).toLocaleString() : 'N/A'}
                </span>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-4 bg-blue-50 rounded-lg">
                <div>
                  <Label className="text-xs text-gray-500">Quote Type</Label>
                  <p className="font-semibold text-lg">{quoteDetails.quote_type || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Requested By</Label>
                  <InquiryCreatorIdentity
                    name={quoteDetails.requester_name || quoteDetails.requester_company || quoteDetails.requester_company_name}
                    profilePicture={quoteDetails.requester_profile_picture}
                    companyName={quoteDetails.requester_company || quoteDetails.requester_company_name}
                    label={null}
                    compact
                    className="mt-1"
                  />
                </div>
              </div>

              {getQuoteInsuranceDisplay(quoteDetails) && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold mb-3">Insurance</h4>
                  <p className="text-sm text-gray-900">{getQuoteInsuranceDisplay(quoteDetails)}</p>
                </div>
              )}

              {Array.isArray(quoteDetails.form_data?.scope) && quoteDetails.form_data.scope.length > 0 && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold mb-4">Scope (Select all that apply)</h4>
                  <div className="flex flex-wrap gap-3">
                    {quoteDetails.form_data.scope.map((item) => (
                      <div key={item} className="px-4 py-2 rounded-md bg-blue-50 text-blue-700 text-sm border border-blue-100">
                        {`✓ ${item}`}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(quoteDetails.origin_port || quoteDetails.destination_port || quoteDetails.form_data?.por || quoteDetails.form_data?.pol || quoteDetails.form_data?.pod || quoteDetails.form_data?.fpod) && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold mb-4 flex items-center">
                    <MapPin size={16} className="mr-2 text-gray-500" />
                    Port Details
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                    <div>
                      <Label className="text-xs text-gray-500">POR (Place of Receipt)</Label>
                      <p className="mt-1 text-gray-900">{quoteDetails.form_data?.porName || quoteDetails.form_data?.por || 'N/A'}{quoteDetails.form_data?.porCountry ? ` — ${quoteDetails.form_data.porCountry}` : ''}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">POL (Port of Loading)</Label>
                      <p className="mt-1 text-gray-900">{quoteDetails.origin_port?.name || quoteDetails.form_data?.polName || quoteDetails.form_data?.pol || 'N/A'}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">POD (Port of Discharge)</Label>
                      <p className="mt-1 text-gray-900">{quoteDetails.destination_port?.name || quoteDetails.form_data?.podName || quoteDetails.form_data?.pod || 'N/A'}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">FPOD (Final Place of Delivery)</Label>
                      <p className="mt-1 text-gray-900">{quoteDetails.form_data?.fpodName || quoteDetails.form_data?.fpod || 'N/A'}{quoteDetails.form_data?.fpodCountry ? ` — ${quoteDetails.form_data.fpodCountry}` : ''}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Incoterm</Label>
                      <p className="mt-1 text-gray-900">{Array.isArray(quoteDetails.incoterm) ? quoteDetails.incoterm.join(', ') : (quoteDetails.incoterm || 'N/A')}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">BL Format</Label>
                      <p className="mt-1 text-gray-900">{quoteDetails.form_data?.blFormat || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              )}

              {(quoteDetails.containers || quoteDetails.form_data?.containers) && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold mb-4 flex items-center">
                    <Package size={16} className="mr-2 text-gray-500" />
                    Standard Containers
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {Object.entries(quoteDetails.form_data?.containers || quoteDetails.containers || {})
                      .filter(([, value]) => Number(value) > 0)
                      .map(([type, value]) => (
                        <div key={type}>
                          <Label className="text-xs text-gray-500">{type}</Label>
                          <p className="text-base font-semibold text-gray-900">{value}</p>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-semibold mb-4">Special Containers</h4>
                {getQuoteSpecialContainerDetails(quoteDetails).length > 0 ? (
                  <div className="space-y-2">
                    {getQuoteSpecialContainerDetails(quoteDetails).map((detail, index) => (
                      <p key={`${detail}-${index}`} className="text-sm text-gray-900">{detail}</p>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500">No Special containers added.</p>
                )}
              </div>

              {((quoteDetails.form_data?.reefer && Object.values(quoteDetails.form_data.reefer).some((value) => Number(value) > 0)) || quoteDetails.form_data?.tempRange20 || quoteDetails.form_data?.tempRange40 || quoteDetails.commodity) && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold mb-4">Reefer & Special Cargo</h4>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    <div>
                      <Label className="text-xs text-gray-500">20&apos; Reefer</Label>
                      <p className="text-base text-gray-900">{quoteDetails.form_data?.reefer?.['20RF'] ?? 0}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Temp Range (20&apos;)</Label>
                      <p className="text-base text-gray-900">{quoteDetails.form_data?.tempRange20 || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">40&apos; Reefer</Label>
                      <p className="text-base text-gray-900">{quoteDetails.form_data?.reefer?.['40RF'] ?? 0}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Temp Range (40&apos;)</Label>
                      <p className="text-base text-gray-900">{quoteDetails.form_data?.tempRange40 || '-'}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Commodity</Label>
                      <p className="text-lg text-gray-900">{quoteDetails.commodity || quoteDetails.form_data?.commodity || 'N/A'}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Hazardous Cargo</Label>
                      <p className="text-lg text-gray-900">{quoteDetails.is_hazardous ? 'Hazardous' : 'Non-Hazardous'}</p>
                    </div>
                  </div>
                </div>
              )}

              {quoteDetails.quote_type === 'Transport' && (
                <>
                  <div className="rounded-lg border p-4">
                    <h3 className="font-semibold text-gray-900 mb-3">Service Type</h3>
                    <p className="text-sm text-gray-900">{quoteDetails.form_data?.serviceType || 'Transport Only'}</p>
                  </div>
                  <div className="rounded-lg border p-4">
                    <h3 className="font-semibold text-gray-900 mb-3">Pickup Location</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div><Label className="text-xs text-gray-500">Address</Label><p>{quoteDetails.form_data?.pickupLocation?.address || 'N/A'}</p></div>
                      <div><Label className="text-xs text-gray-500">City</Label><p>{quoteDetails.form_data?.pickupLocation?.city || quoteDetails.origin_city || 'N/A'}</p></div>
                      <div><Label className="text-xs text-gray-500">Country</Label><p>{quoteDetails.form_data?.pickupLocation?.country || 'N/A'}</p></div>
                      <div><Label className="text-xs text-gray-500">Postal Code</Label><p>{quoteDetails.form_data?.pickupLocation?.postalCode || 'N/A'}</p></div>
                    </div>
                  </div>
                  <div className="rounded-lg border p-4">
                    <h3 className="font-semibold text-gray-900 mb-3">Delivery Location</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div><Label className="text-xs text-gray-500">Address</Label><p>{quoteDetails.form_data?.deliveryLocation?.address || 'N/A'}</p></div>
                      <div><Label className="text-xs text-gray-500">City</Label><p>{quoteDetails.form_data?.deliveryLocation?.city || quoteDetails.destination_city || 'N/A'}</p></div>
                      <div><Label className="text-xs text-gray-500">Country</Label><p>{quoteDetails.form_data?.deliveryLocation?.country || 'N/A'}</p></div>
                      <div><Label className="text-xs text-gray-500">Postal Code</Label><p>{quoteDetails.form_data?.deliveryLocation?.postalCode || 'N/A'}</p></div>
                    </div>
                  </div>
                </>
              )}

              {quoteDetails.quote_type === 'Air' && (
                <div className="rounded-lg border p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Air Freight Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                    <div><Label className="text-xs text-gray-500">Origin Airport</Label><p>{quoteDetails.form_data?.origin || quoteDetails.origin_city || 'N/A'}</p></div>
                    <div><Label className="text-xs text-gray-500">Destination Airport</Label><p>{quoteDetails.form_data?.destination || quoteDetails.destination_city || 'N/A'}</p></div>
                    <div><Label className="text-xs text-gray-500">Chargeable Weight</Label><p>{quoteDetails.chargeable_weight || quoteDetails.form_data?.chargeableWeight || 'N/A'}</p></div>
                    <div><Label className="text-xs text-gray-500">Total Weight</Label><p>{quoteDetails.weight_kg || quoteDetails.form_data?.totalWeight || 'N/A'}</p></div>
                    <div><Label className="text-xs text-gray-500">Volume</Label><p>{quoteDetails.volume_cbm || quoteDetails.form_data?.cargoVolume || 'N/A'}</p></div>
                    <div><Label className="text-xs text-gray-500">Hazardous</Label><p>{quoteDetails.is_hazardous ? 'Yes' : 'No'}</p></div>
                  </div>
                </div>
              )}

              {quoteDetails.quote_type === 'Warehousing' && (
                <div className="rounded-lg border p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Warehousing Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                    <div><Label className="text-xs text-gray-500">Location</Label><p>{quoteDetails.form_data?.location?.city || quoteDetails.form_data?.warehouseLocation || quoteDetails.origin_city || 'N/A'}</p></div>
                    <div><Label className="text-xs text-gray-500">Storage Type</Label><p>{quoteDetails.form_data?.storageType || 'N/A'}</p></div>
                    <div><Label className="text-xs text-gray-500">Area Required</Label><p>{quoteDetails.form_data?.areaRequired || 'N/A'}</p></div>
                    <div><Label className="text-xs text-gray-500">Pallets</Label><p>{quoteDetails.form_data?.palletCount || 'N/A'}</p></div>
                    <div><Label className="text-xs text-gray-500">Weight</Label><p>{quoteDetails.weight_kg || quoteDetails.form_data?.cargoWeight || 'N/A'}</p></div>
                    <div><Label className="text-xs text-gray-500">Volume</Label><p>{quoteDetails.volume_cbm || quoteDetails.form_data?.cargoVolume || 'N/A'}</p></div>
                  </div>
                </div>
              )}

              {(quoteDetails.quote_type === 'Ocean FCL' || quoteDetails.quote_type === 'Ocean LCL' || quoteDetails.quote_type === 'Breakbulk') && (
                <div className="rounded-lg border p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">{quoteDetails.quote_type} Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                    <div><Label className="text-xs text-gray-500">POL</Label><p>{quoteDetails.origin_port?.name || quoteDetails.form_data?.polName || quoteDetails.form_data?.pol || 'N/A'}</p></div>
                    <div><Label className="text-xs text-gray-500">POD</Label><p>{quoteDetails.destination_port?.name || quoteDetails.form_data?.podName || quoteDetails.form_data?.pod || 'N/A'}</p></div>
                    <div><Label className="text-xs text-gray-500">Commodity</Label><p>{quoteDetails.commodity || quoteDetails.form_data?.commodity || 'N/A'}</p></div>
                    <div><Label className="text-xs text-gray-500">Incoterm</Label><p>{Array.isArray(quoteDetails.incoterm) ? quoteDetails.incoterm.join(', ') : (quoteDetails.incoterm || 'N/A')}</p></div>
                    <div><Label className="text-xs text-gray-500">Free Time POL</Label><p>{quoteDetails.form_data?.polFreeTime || 'N/A'}</p></div>
                    <div><Label className="text-xs text-gray-500">Free Time POD</Label><p>{quoteDetails.form_data?.podFreeTime || 'N/A'}</p></div>
                    <div><Label className="text-xs text-gray-500">BL Type</Label><p>{quoteDetails.form_data?.blType || 'N/A'}</p></div>
                    <div><Label className="text-xs text-gray-500">BL Format</Label><p>{quoteDetails.form_data?.blFormat || 'N/A'}</p></div>
                    <div><Label className="text-xs text-gray-500">Hazardous</Label><p>{quoteDetails.is_hazardous ? 'Yes' : 'No'}</p></div>
                  </div>
                </div>
              )}

              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center mb-3">
                  <DollarSign size={16} className="mr-2 text-gray-500" />
                  <h3 className="font-semibold text-gray-900">Requirements</h3>
                </div>
                {Array.isArray(quoteDetails.sender_line_items) && quoteDetails.sender_line_items.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-white border-b">
                        <tr className="text-xs text-gray-500">
                          <th className="px-2 py-2 text-left">Description</th>
                          <th className="px-2 py-2 text-left">Unit</th>
                          <th className="px-2 py-2 text-right">Qty</th>
                          <th className="px-2 py-2 text-right">Unit Price</th>
                          <th className="px-2 py-2 text-right">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {quoteDetails.sender_line_items.map((item, index) => (
                          <tr key={`${item.description}-${index}`} className={`border-b ${item.is_at_actuals ? 'bg-amber-50' : ''}`}>
                            <td className="px-2 py-2">{item.description}</td>
                            <td className="px-2 py-2">{item.unit || 'N/A'}</td>
                            <td className="px-2 py-2 text-right">{item.quantity || 1}</td>
                            <td className="px-2 py-2 text-right">${Number(item.unit_price || 0).toFixed(2)}</td>
                            <td className="px-2 py-2 text-right font-medium">
                              {item.is_at_actuals ? 'At Actuals' : `$${((item.unit_price || 0) * (item.quantity || 1)).toFixed(2)}`}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-white border-t font-medium">
                        <tr>
                          <td colSpan={4} className="px-2 py-2 text-right">Total</td>
                          <td className="px-2 py-2 text-right text-blue-600">${calculateTotals(quoteDetails.sender_line_items).total}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ) : (
                  <p className="text-gray-500">No charge items added yet.</p>
                )}
              </div>

              {(() => {
                const relatedBid = getConversationRelatedBid(quoteDetails, selectedConversation);
                if (!relatedBid) return null;

                const relatedBidName =
                  relatedBid.bidder_name ||
                  (selectedConversation?.memberId === quoteDetails?.requester_id
                    ? 'Your latest quotation'
                    : `${selectedConversation?.otherParticipantName || selectedConversation?.company || 'Participant'} quotation`);

                return (
                  <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                    <div className="flex items-center mb-3">
                      <DollarSign size={16} className="mr-2 text-purple-600" />
                      <div>
                        <h3 className="font-semibold text-purple-900">Latest Shared Quote</h3>
                        <p className="text-xs text-purple-700">{relatedBidName}</p>
                      </div>
                    </div>

                    {Array.isArray(relatedBid.line_items) && relatedBid.line_items.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-white border-b">
                            <tr className="text-xs text-gray-500">
                              <th className="px-2 py-2 text-left">Description</th>
                              <th className="px-2 py-2 text-left">Unit</th>
                              <th className="px-2 py-2 text-right">Qty</th>
                              <th className="px-2 py-2 text-right">Unit Price</th>
                              <th className="px-2 py-2 text-right">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {relatedBid.line_items.map((item, index) => (
                              <tr key={`${item.description}-${index}`} className={`border-b ${item.is_at_actuals ? 'bg-amber-50' : ''}`}>
                                <td className="px-2 py-2">{item.description}</td>
                                <td className="px-2 py-2">{item.unit || 'N/A'}</td>
                                <td className="px-2 py-2 text-right">{item.quantity || 1}</td>
                                <td className="px-2 py-2 text-right">${Number(item.unit_price || 0).toFixed(2)}</td>
                                <td className="px-2 py-2 text-right font-medium">
                                  {item.is_at_actuals ? 'At Actuals' : `$${((item.unit_price || 0) * (item.quantity || 1)).toFixed(2)}`}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-white border-t font-medium">
                            <tr>
                              <td colSpan={4} className="px-2 py-2 text-right">Total</td>
                              <td className="px-2 py-2 text-right text-purple-700">${calculateTotals(relatedBid.line_items).total}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    ) : (
                      <p className="text-gray-500">No quoted charge items shared yet.</p>
                    )}

                    {(relatedBid.remarks || relatedBid.notes) && (
                      <div className="mt-3">
                        <Label className="text-xs text-gray-500">Remarks</Label>
                        <p className="mt-1 whitespace-pre-wrap text-sm text-gray-800">{relatedBid.remarks || relatedBid.notes}</p>
                      </div>
                    )}
                  </div>
                );
              })()}

              {(quoteDetails.cargo_description || quoteDetails.form_data?.specialRequirements || quoteDetails.form_data?.notes) && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <Label className="text-xs text-gray-500">Notes</Label>
                  <p className="mt-2 whitespace-pre-wrap text-sm text-gray-800">
                    {quoteDetails.cargo_description || quoteDetails.form_data?.specialRequirements || quoteDetails.form_data?.notes}
                  </p>
                </div>
              )}

              <div className="p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center mb-3 text-blue-800">
                  <FileText size={16} className="mr-2" />
                  <h3 className="font-semibold">Special Instructions</h3>
                </div>
                {Array.isArray(quoteDetails.special_instructions) && quoteDetails.special_instructions.length > 0 ? (
                  <div className="space-y-2">
                    {quoteDetails.special_instructions.map((instruction, index) => (
                      <div key={instruction.id || instruction.instruction_number || index} className="flex items-start gap-2 text-sm text-gray-800">
                        <span className="font-medium text-gray-600">{instruction.instruction_number ? `${instruction.instruction_number}:` : `1.${index + 1}:`}</span>
                        <span>{instruction.instruction_text || 'N/A'}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500">No special instructions added.</p>
                )}
              </div>

              <div className="p-4 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-700"><strong>{quoteDetails.responses_count || 0}</strong> response(s) received</span>
              </div>

              {quoteDetails.status === 'Nominated' && Array.isArray(quoteDetails.bids) && quoteDetails.nominated_bid_id && (
                <div className="rounded-lg border border-purple-200 bg-purple-50 p-4">
                  <h3 className="font-semibold text-purple-900 mb-3">Nominated Quote</h3>
                  {(() => {
                    const nominatedBid = quoteDetails.bids.find((bid) => bid.bid_id === quoteDetails.nominated_bid_id);
                    if (!nominatedBid) {
                      return <p className="text-sm text-gray-600">Nominated bid details could not be loaded.</p>;
                    }

                    return (
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          <div>
                            <Label className="text-xs text-gray-500">Nominated provider</Label>
                            <p className="font-semibold text-gray-900">
                              {quoteDetails.nominated_company || quoteDetails.nominated_member_id || 'N/A'}
                            </p>
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">Validity</Label>
                            <p className="text-gray-900">
                              {nominatedBid.quote_valid_until
                                ? `Valid until ${new Date(nominatedBid.quote_valid_until).toLocaleDateString()}`
                                : `${nominatedBid.validity_days || 30} days`}
                            </p>
                          </div>
                        </div>
                        {Array.isArray(nominatedBid.line_items) && nominatedBid.line_items.length > 0 && (
                          <div className="overflow-x-auto rounded border bg-white">
                            <table className="w-full text-sm">
                              <thead className="bg-gray-50 border-b">
                                <tr className="text-xs text-gray-500">
                                  <th className="px-3 py-2 text-left">Description</th>
                                  <th className="px-3 py-2 text-left">Unit</th>
                                  <th className="px-3 py-2 text-right">Qty</th>
                                  <th className="px-3 py-2 text-right">Unit Price</th>
                                  <th className="px-3 py-2 text-right">Total</th>
                                </tr>
                              </thead>
                              <tbody>
                                {nominatedBid.line_items.map((item, index) => (
                                  <tr key={`${item.description}-${index}`} className="border-b border-gray-100">
                                    <td className="px-3 py-2">{item.description}</td>
                                    <td className="px-3 py-2">{item.unit || '—'}</td>
                                    <td className="px-3 py-2 text-right">{item.quantity ?? 1}</td>
                                    <td className="px-3 py-2 text-right">${Number(item.unit_price || 0).toFixed(2)}</td>
                                    <td className="px-3 py-2 text-right font-medium">
                                      {item.is_at_actuals ? 'At Actuals' : `$${((item.unit_price || 0) * (item.quantity ?? 1)).toFixed(2)}`}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot className="bg-gray-50 border-t font-medium">
                                <tr>
                                  <td colSpan={4} className="px-3 py-2 text-right">Total</td>
                                  <td className="px-3 py-2 text-right text-purple-700">${calculateTotals(nominatedBid.line_items).total}</td>
                                </tr>
                              </tfoot>
                            </table>
                          </div>
                        )}
                        {(nominatedBid.remarks || nominatedBid.notes) && (
                          <div>
                            <Label className="text-xs text-gray-500">Remarks</Label>
                            <p className="mt-1 whitespace-pre-wrap text-sm text-gray-800">{nominatedBid.remarks || nominatedBid.notes}</p>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}

              <div className="flex justify-end pt-2">
                <Button variant="outline" onClick={() => setShowQuoteDetailsDialog(false)}>
                  Close
                </Button>
              </div>
            </div>
          ) : (
            <div className="py-10 text-center text-sm text-gray-500">Quote details are not available.</div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MemberChat;
